import { useTranslation } from 'react-i18next';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISlopedGabionResults } from '../../../api/IApiResponse';
import { selectStillingBasinAndSoilDataByProject } from '../../../selector/conversion';
import { IProjectMacra2 } from '../../../store/project/projectSlice';
import { IHydraulicDataSlopedWeirForm } from '../../InputForm/HydraulicData/IHydraulicData';
import { IWeirGeometrySlopedWeirForm } from '../../InputForm/WeirGeometry/IWeirGeometry';
import '../StaticResults/StaticResults.scss';

interface IBasinDesignSlopedProps {}

const BasinDesignSloped = (props: IBasinDesignSlopedProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<ISlopedGabionResults> = useAppSelector(selectResults as any);
    const project: IInitialState<IProjectMacra2> = useAppSelector(selectProject as any);
    const stillingBasinSoilData = useAppSelector(selectStillingBasinAndSoilDataByProject);

    const projectInfo = useAppSelector(selectProjectInfo);

    return (
        <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85 w-100 static-results'>
            <table className='tableOutputData'>
                <thead>
                    <tr>
                        <th>{t('output_hydraulic_sloped_chute_and_basin_design_output')}</th>
                        <th>{t('output_symbol')}</th>
                        <th>{t('output_value')}</th>
                        <th>{t('output_unit')}</th>
                        <th>{t('output_check')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_sloped_stilling_basin_width')}</td>
                        <td>
                            <SmallLetter normal='W' small='b' />
                        </td>
                        <td>{roundToTwo(stillingBasinSoilData?.stillingBasin?.width)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_sloped_chute_lining_thickness')}</td>
                        <td>
                            <SmallLetter normal='t' small='c' />
                        </td>
                        <td>{roundToTwo((project.data?.weirGeometry as IWeirGeometrySlopedWeirForm).liningThickness)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_sloped_minimum_length_stilling_basin')}</td>
                        <td>
                            <SmallLetter normal='L' small='b_min' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.jumpLength)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {stillingBasinSoilData?.stillingBasin?.poolLength! >= roundToTwo(results.data?.basinDesign.jumpLength) ? (
                                <i className='fa-solid fa-check  d-flex  justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_sloped_minimum_counterweir_height')}</td>
                        <td>
                            <SmallLetter normal='f' small='c_min' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.minCounterweirHeight)}</td>
                        <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {stillingBasinSoilData?.stillingBasin?.counterweirElevation! >= roundToTwo(results.data?.basinDesign.minCounterweirHeight) ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_lining_material')}</td>
                        <td colSpan={4}>
                            {stillingBasinSoilData?.stillingBasin?.material.name} - t={roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].thickness)}{' '}
                            {conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - d50=
                            {roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].rockD50)} {conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]} - Cu=
                            {roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].cu)}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_sloped_basin_lining_thickness')}</td>
                        <td>
                            <SmallLetter normal='t' small='b' />
                        </td>
                        <td>{roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].thickness)}</td>
                        <td>{conversionLabel.m_in[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_sloped_velocity_under_lining')}</td>
                        <td>
                            <SmallLetter normal='v' small='1' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.residualVelocityUnderGeotextile)}</td>
                        <td>{conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {(project.data?.hydraulicData as IHydraulicDataSlopedWeirForm).soilAdmissibleVelocity >= roundToTwo(results.data?.basinDesign.residualVelocityUnderGeotextile) ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section')}</td>
                        <td>
                            <SmallLetter normal='τ' small='c' />
                        </td>
                        <td>{roundToTwo(results.data?.basinDesign.shearStress)}</td>
                        <td>{conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            {roundToTwo(results.data?.basinDesign.shearStress) < stillingBasinSoilData?.stillingBasin?.material.physicalData![0].allowShearStress! ? (
                                <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />
                            ) : (
                                <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />
                            )}
                        </td>
                    </tr>
                    <tr className='bg-white'>
                        <td>{t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining')}</td>
                        <td>
                            <SmallLetter normal='τ' small='all' />
                        </td>
                        <td>{roundToTwo(stillingBasinSoilData?.stillingBasin?.material.physicalData![0].allowShearStress)}</td>
                        <td>{conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number]}</td>
                        <td>
                            <div className='d-flex justify-content-center'>-</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default BasinDesignSloped;
